import Modals from 'application/ui/modal/modal'
import { validateForm } from 'application/utils/form-validation'
import EmblaCarousel from 'embla-carousel'
import type { EmblaCarouselType } from 'embla-carousel'

const MODAL_NAME = 'profile_booking_modal'
const ADD_REVIEW_MODAL_NAME = 'add_review_modal'
const RESULT_MODAL_NAME = 'result_modal'
const ADD_REVIEW_MODAL_OPEN_DELAY = 300
const RESULT_MODAL_OPEN_DELAY = 300
type SliderDirection = 'prev' | 'next'
const BOOKINGS_PAGE_SIZE = 12
const REVIEWS_PAGE_SIZE = 5
const REVIEW_SUBMIT_ERROR =
  'Не удалось отправить отзыв. Обновите страницу и попробуйте ещё раз'
const REVIEW_SHOW_BUTTON_VISIBLE_CLASS = 'is-visible'
const REVIEW_DESCRIPTION_EXPANDED_CLASS = 'is-expanded'

type SocialItem = {
  name?: string
  url?: string
  icon?: string
}

type CardData = {
  questId?: string
  questName?: string
  questUrl?: string
  imageSrc?: string
  metro?: string
  metroColor?: string
  gamersText?: string
  length?: string
  date?: string
  time?: string
  gamers?: string
  status?: string
  awaitingReview?: boolean
  phone?: string
  address?: string
  addressDescription?: string
  socials?: SocialItem[]
}

let currentCardData: CardData | null = null

const REVIEW_CRITERIA = ['general', 'ambiance', 'staff', 'consistency']
const REVIEW_CAPTIONS: Record<string, string> = {
  '1': 'Ужасно',
  '2': 'Сносно',
  '3': 'Нормально',
  '4': 'Хорошо',
  '5': 'Отлично'
}
// Отзыв, который сейчас правят или удаляют из карточки в кабинете.
let editingReviewId: string | null = null
let deletingReviewCard: HTMLElement | null = null
// Пересчёт видимости и кнопки «Показать ещё» после удаления отзыва.
let refreshReviewsVisibility: () => void = () => {}

export function initProfilePage() {
  initProfileQuestsSliders()
  initProfileBookingsFilter()
  const updateProfileReviewShowButtons = initProfileReviewShowButtons()

  initProfileReviewsShowMore(updateProfileReviewShowButtons)
  initProfileBookingModal()
  initAddReviewModal()
  initAddReviewForm()
  initProfileReviewActions()
  initProfileSettingsForm()
}

// Карточка отзыва в кабинете: «Изменить» открывает ту же модалку с текущими
// оценками и текстом (PATCH), «Удалить» — после подтверждения (DELETE).
function initProfileReviewActions() {
  document.addEventListener('click', (event) => {
    const target = event.target as HTMLElement
    const editButton = target.closest<HTMLElement>(
      '[data-profile-review-edit-button]'
    )
    if (editButton) {
      event.preventDefault()
      openReviewForEdit(editButton)
      return
    }
    const deleteButton = target.closest<HTMLElement>(
      '[data-profile-review-delete-button]'
    )
    if (deleteButton) {
      deletingReviewCard = deleteButton.closest<HTMLElement>(
        '[data-profile-review-id]'
      )
      return
    }
    const confirmButton = target.closest<HTMLElement>(
      '[data-modal-name="profile_confirm_modal"] [data-close-confirm]'
    )
    if (confirmButton) void deleteReview()
  })
}

function openReviewForEdit(button: HTMLElement) {
  const card = button.closest<HTMLElement>('[data-profile-review-id]')
  const form = getAddReviewForm()
  if (!card || !form) return
  const d = card.dataset
  editingReviewId = d.profileReviewId ?? null
  // Закрытие модалки не сбрасывает форму: сначала снимаем прежний выбор,
  // иначе у отзыва без какого-то критерия остался бы чужой radio.
  form
    .querySelectorAll<HTMLInputElement>('input[type="radio"][name^="review["]')
    .forEach((input) => {
      input.checked = false
    })
  fillAddReviewModal({
    questId: d.profileReviewQuestId,
    questName: d.profileReviewTitle,
    questUrl: d.profileReviewQuestUrl,
    imageSrc: d.profileReviewImage
  })
  REVIEW_CRITERIA.forEach((criterion) => {
    const key = `profileReview${criterion[0].toUpperCase()}${criterion.slice(1)}`
    const input = form.querySelector<HTMLInputElement>(
      `input[name="review[${criterion}]"][value="${d[key]}"]`
    )
    if (input) input.checked = true
  })
  const textarea = getAddReviewTextarea(form)
  if (textarea) {
    textarea.value =
      card
        .querySelector<HTMLElement>('[data-profile-review-description]')
        ?.textContent?.trim() ?? ''
  }
  setReviewFormError(form, '')
  setReviewFormMode(form, 'edit')
  updateReviewCaptions(form)
  updateAddReviewSubmitState(form)
  Modals.getInstance().openModal(ADD_REVIEW_MODAL_NAME)
}

function setReviewFormMode(form: HTMLFormElement, mode: 'create' | 'edit') {
  const title = document.querySelector<HTMLElement>('[data-review-modal-title]')
  const submit = form.querySelector<HTMLButtonElement>('[data-review-submit]')
  if (title) {
    title.textContent =
      mode === 'edit' ? 'Измените отзыв о квесте' : 'Оставьте отзыв о квесте'
  }
  if (submit) {
    submit.textContent = mode === 'edit' ? 'Сохранить отзыв' : 'Отправить отзыв'
  }
  if (mode === 'create') editingReviewId = null
}

async function deleteReview() {
  const card = deletingReviewCard
  deletingReviewCard = null
  const id = card?.dataset.profileReviewId
  if (!card || !id) return
  try {
    const response = await fetch(`/reviews/${id}`, {
      method: 'DELETE',
      headers: reviewRequestHeaders()
    })
    if (!response.ok) throw new Error('Не удалось удалить отзыв')
    // Счётчик и пагинация считают элементы списка, а не карточки внутри них.
    const item = card.closest<HTMLElement>('[data-profile-review-item]') ?? card
    item.remove()
    // Освободившееся место занимают скрытые отзывы, счётчик и «Показать ещё»
    // пересчитываются по оставшимся (ревью Codex 14.09.2026).
    refreshReviewsVisibility()
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('profile review delete error', error)
  }
}

function reviewRequestHeaders(): Record<string, string> {
  const csrfToken = (
    document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null
  )?.content
  return {
    Accept: 'application/json',
    ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {})
  }
}

function getAddReviewForm(): HTMLFormElement | null {
  return document.querySelector<HTMLFormElement>('[data-profile-review-form]')
}

// Подпись словами под выбранной оценкой: ошибка со звёздами видна до отправки.
function updateReviewCaptions(form: HTMLFormElement) {
  form.querySelectorAll<HTMLElement>('fieldset').forEach((fieldset) => {
    const checked = fieldset.querySelector<HTMLInputElement>(
      'input[type="radio"]:checked'
    )
    const caption = fieldset.querySelector<HTMLElement>('[data-rating-caption]')
    if (caption) {
      caption.textContent = checked
        ? (REVIEW_CAPTIONS[checked.value] ?? '')
        : ''
    }
  })
}

function hasAllReviewRatings(form: HTMLFormElement): boolean {
  return REVIEW_CRITERIA.every((criterion) =>
    form.querySelector(`input[name="review[${criterion}]"]:checked`)
  )
}

function initProfileBookingsFilter() {
  const form = document.querySelector<HTMLFormElement>(
    '[data-profile-bookings-filter-form]'
  )
  const list = document.querySelector<HTMLElement>(
    '[data-profile-bookings-list]'
  )
  const showMoreButton = document.querySelector<HTMLButtonElement>(
    '[data-profile-bookings-show-more]'
  )
  let visibleCount = BOOKINGS_PAGE_SIZE

  if (!form || !list) return

  const items = Array.from(
    list.querySelectorAll<HTMLElement>('[data-profile-booking-item]')
  )

  const applyFilter = () => {
    const formData = new FormData(form)
    const activeType = formData.get('quests')?.toString() || 'all'
    const filteredItems = items.filter((item) => {
      const itemType = item.dataset.profileBookingItem

      return activeType === 'all' || itemType === activeType
    })

    items.forEach((item) => {
      const shouldShow =
        filteredItems.includes(item) &&
        filteredItems.indexOf(item) < visibleCount

      item.classList.toggle('is-hidden', !shouldShow)
    })

    showMoreButton?.classList.toggle(
      'is-hidden',
      visibleCount >= filteredItems.length
    )
  }

  form.addEventListener('change', () => {
    visibleCount = BOOKINGS_PAGE_SIZE
    applyFilter()
  })
  showMoreButton?.addEventListener('click', () => {
    visibleCount += BOOKINGS_PAGE_SIZE
    applyFilter()
  })
  setProfileBookingsCounts(items)
  applyFilter()
}

function setProfileBookingsCounts(items: HTMLElement[]) {
  setProfileBookingsCount('[data-profile-bookings-total-count]', items.length)
  setProfileBookingsCount(
    '[data-profile-bookings-upcoming-count]',
    countProfileBookingItems(items, 'upcoming')
  )
  setProfileBookingsCount(
    '[data-profile-bookings-past-count]',
    countProfileBookingItems(items, 'past')
  )
}

function countProfileBookingItems(items: HTMLElement[], type: string): number {
  return items.filter((item) => item.dataset.profileBookingItem === type).length
}

function setProfileBookingsCount(selector: string, value: number) {
  const element = document.querySelector<HTMLElement>(selector)

  if (element) {
    element.textContent = value.toString()
  }
}

function initProfileReviewsShowMore(updateReviewShowButtons = () => {}) {
  const list = document.querySelector<HTMLElement>(
    '[data-profile-reviews-list]'
  )
  const showMoreButton = document.querySelector<HTMLButtonElement>(
    '[data-profile-reviews-show-more]'
  )
  let visibleCount = REVIEWS_PAGE_SIZE

  if (!list) return

  // Элементы читаем заново при каждом пересчёте: после удаления отзыва список
  // меняется, а видимость и кнопка должны считаться по оставшимся.
  const currentItems = () =>
    Array.from(list.querySelectorAll<HTMLElement>('[data-profile-review-item]'))

  const applyVisibility = () => {
    const items = currentItems()
    items.forEach((item, index) => {
      item.classList.toggle('is-hidden', index >= visibleCount)
    })

    showMoreButton?.classList.toggle('is-hidden', visibleCount >= items.length)
    setProfileReviewsCount(items.length)
    updateReviewShowButtons()
  }

  refreshReviewsVisibility = applyVisibility
  showMoreButton?.addEventListener('click', () => {
    visibleCount += REVIEWS_PAGE_SIZE
    applyVisibility()
  })
  applyVisibility()
}

function initProfileReviewShowButtons(): () => void {
  const buttons = Array.from(
    document.querySelectorAll<HTMLButtonElement>(
      '[data-profile-review-show-button]'
    )
  )

  if (!buttons.length) return () => {}

  const updateButtons = () => {
    buttons.forEach(updateProfileReviewShowButton)
  }

  buttons.forEach((button) => {
    button.addEventListener('click', () => {
      const description = getProfileReviewDescription(button)

      if (!description) return

      description.classList.toggle(REVIEW_DESCRIPTION_EXPANDED_CLASS)
      updateProfileReviewShowButton(button)
    })
  })

  window.addEventListener('resize', updateButtons)
  document.fonts?.ready.then(updateButtons)
  requestAnimationFrame(updateButtons)
  updateButtons()

  return updateButtons
}

function updateProfileReviewShowButton(button: HTMLButtonElement) {
  const description = getProfileReviewDescription(button)

  if (!description) {
    button.classList.remove(REVIEW_SHOW_BUTTON_VISIBLE_CLASS)

    return
  }

  const isExpanded = description.classList.contains(
    REVIEW_DESCRIPTION_EXPANDED_CLASS
  )
  const hasOverflow =
    description.scrollHeight - description.clientHeight >
    getProfileReviewDescriptionOverflowTolerance(description)

  button.classList.toggle(
    REVIEW_SHOW_BUTTON_VISIBLE_CLASS,
    hasOverflow || isExpanded
  )
  button.setAttribute('aria-expanded', isExpanded ? 'true' : 'false')
  button.textContent = isExpanded
    ? getProfileReviewButtonText(button, 'close')
    : getProfileReviewButtonText(button, 'open')
}

function getProfileReviewDescription(button: HTMLElement) {
  const review = button.closest<HTMLElement>('[data-profile-review-id]')

  return (
    review?.querySelector<HTMLElement>('[data-profile-review-description]') ||
    null
  )
}

function getProfileReviewDescriptionOverflowTolerance(
  description: HTMLElement
) {
  const lineHeight = parseFloat(getComputedStyle(description).lineHeight)

  if (Number.isNaN(lineHeight)) return 1

  return lineHeight / 2
}

function getProfileReviewButtonText(
  button: HTMLButtonElement,
  state: 'open' | 'close'
) {
  const text =
    state === 'open' ? button.dataset.openText : button.dataset.closeText

  return text || button.textContent?.trim() || ''
}

function setProfileReviewsCount(value: number) {
  const element = document.querySelector<HTMLElement>(
    '[data-profile-reviews-total-count]'
  )

  if (element) {
    element.textContent = value.toString()
  }
}

function initProfileSettingsForm() {
  document
    .querySelectorAll<HTMLFormElement>('[data-profile-settings-form]')
    .forEach((form) => {
      form.addEventListener('submit', (event) => {
        if (!validateForm(form)) event.preventDefault()
      })
    })
}

function initProfileQuestsSliders() {
  const sliders = document.querySelectorAll<HTMLElement>(
    '[data-profile-quests-slider]'
  )

  sliders.forEach((slider) => {
    initProfileQuestsSlider(slider)
  })
}

function initProfileQuestsSlider(sliderEl: HTMLElement) {
  // eslint-disable-next-line new-cap
  const slider: EmblaCarouselType = EmblaCarousel(sliderEl, {
    align: 'start',
    containScroll: 'trimSnaps',
    dragFree: true
  })

  const updateButtonsVisibility = () => {
    toggleSliderButton(sliderEl, 'prev', slider.canScrollPrev())
    toggleSliderButton(sliderEl, 'next', slider.canScrollNext())
  }

  slider.on('init', updateButtonsVisibility)
  slider.on('select', updateButtonsVisibility)
  slider.on('reInit', updateButtonsVisibility)
  updateButtonsVisibility()

  sliderEl.addEventListener('click', (event: MouseEvent) => {
    const target = event.target as HTMLElement
    const button = target.closest<HTMLElement>('[data-slider-button]')

    if (!button || !sliderEl.contains(button)) return

    if (button.dataset.dir === 'next') {
      slider.scrollNext()
    } else if (button.dataset.dir === 'prev') {
      slider.scrollPrev()
    }
  })
}

function toggleSliderButton(
  sliderEl: HTMLElement,
  direction: SliderDirection,
  isVisible: boolean
) {
  const button = sliderEl.querySelector<HTMLElement>(
    `[data-slider-button][data-dir="${direction}"]`
  )

  button?.classList.toggle('is-hidden', !isVisible)
}

function initProfileBookingModal() {
  const triggers = document.querySelectorAll<HTMLElement>(
    '[data-open-profile-modal]'
  )

  if (!triggers.length) return

  triggers.forEach((trigger) => {
    trigger.addEventListener('click', onProfileModalOpen)
  })
}

function initAddReviewModal() {
  const triggers = document.querySelectorAll<HTMLElement>(
    `[data-open-modal="${ADD_REVIEW_MODAL_NAME}"], [data-open-add-review-from-profile-modal]`
  )

  if (!triggers.length) return

  triggers.forEach((trigger) => {
    trigger.addEventListener('click', onAddReviewModalOpen)
  })
}

function initAddReviewForm() {
  const form = document.querySelector<HTMLFormElement>(
    '[data-profile-review-form]'
  )

  if (!form) return

  const textarea = getAddReviewTextarea(form)

  textarea?.addEventListener('input', () => {
    setReviewFormError(form, '')
    updateAddReviewSubmitState(form)
  })
  form.addEventListener('change', () => {
    setReviewFormError(form, '')
    updateReviewCaptions(form)
    updateAddReviewSubmitState(form)
  })
  form.addEventListener('reset', () => {
    setTimeout(() => {
      setReviewFormError(form, '')
      setReviewFormMode(form, 'create')
      updateReviewCaptions(form)
      updateAddReviewSubmitState(form)
    })
  })
  form.addEventListener('submit', onAddReviewFormSubmit)
  updateAddReviewSubmitState(form)
}

function readCardData(card: HTMLElement): CardData {
  const d = card.dataset

  return {
    questId: d.profileBookingModalQuestId,
    questName: d.profileBookingModalQuestName,
    questUrl: d.profileBookingModalQuestUrl,
    imageSrc: d.profileBookingModalImageSrc,
    metro: d.profileBookingModalMetro,
    metroColor: d.profileBookingModalMetroColor,
    gamersText: d.profileBookingModalGamersText,
    length: d.profileBookingModalLength,
    date: d.profileBookingModalDate,
    time: d.profileBookingModalTime,
    gamers: d.profileBookingModalGamers,
    status: d.profileBookingModalStatus,
    awaitingReview: d.profileBookingModalAwaitingReview === 'true',
    phone: d.profileBookingModalPhone,
    address: d.profileBookingModalAddress,
    addressDescription: d.profileBookingModalAddressDescription,
    socials: parseSocials(d.profileBookingModalSocials)
  }
}

function onProfileModalOpen(event: MouseEvent) {
  event.preventDefault()

  const trigger = event.currentTarget as HTMLElement
  const card = trigger.closest<HTMLElement>(
    '[data-profile-booking-modal-status]'
  )

  if (!card) return

  currentCardData = readCardData(card)
  fillProfileModal(currentCardData)
  Modals.getInstance().openModal(MODAL_NAME, event)
}

function onAddReviewModalOpen(event: MouseEvent) {
  const trigger = event.currentTarget as HTMLElement
  const card = trigger.closest<HTMLElement>(
    '[data-profile-booking-modal-status]'
  )
  const data = card ? readCardData(card) : currentCardData
  if (!data) return
  const form = getAddReviewForm()
  if (form) {
    form.reset()
    setReviewFormMode(form, 'create')
  }
  fillAddReviewModal(data)

  if (!trigger.hasAttribute('data-open-add-review-from-profile-modal')) return

  event.preventDefault()

  const modals = Modals.getInstance()
  modals.closeModal(MODAL_NAME)
  window.setTimeout(() => {
    modals.openModal(ADD_REVIEW_MODAL_NAME, event)
  }, ADD_REVIEW_MODAL_OPEN_DELAY)
}

async function onAddReviewFormSubmit(event: SubmitEvent) {
  event.preventDefault()

  const form = event.currentTarget as HTMLFormElement
  if (!hasAddReviewText(form) || !hasAllReviewRatings(form)) return

  const formData = new FormData(form)
  const reviewId = editingReviewId

  updateAddReviewSubmitState(form, true)

  try {
    const action = reviewId
      ? `/reviews/${reviewId}`
      : form.dataset.createAction || form.getAttribute('action') || ''

    const response = await fetch(action, {
      method: reviewId ? 'PATCH' : 'POST',
      headers: reviewRequestHeaders(),
      body: formData
    })

    if (!response.ok) {
      throw new ReviewSubmitError(await readReviewError(response))
    }

    if (reviewId) {
      // Исправленный отзыв снова на модерации: список перерисуем с сервера.
      window.setTimeout(() => window.location.reload(), RESULT_MODAL_OPEN_DELAY)
    }
    form.reset()
    openReviewResultModal()
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('profile review fetch error', error)
    setReviewFormError(
      form,
      error instanceof ReviewSubmitError ? error.message : REVIEW_SUBMIT_ERROR
    )
  } finally {
    updateAddReviewSubmitState(form)
  }
}

class ReviewSubmitError extends Error {}

async function readReviewError(response: Response): Promise<string> {
  try {
    const data = (await response.json()) as { error?: string }

    return data.error || REVIEW_SUBMIT_ERROR
  } catch {
    return REVIEW_SUBMIT_ERROR
  }
}

function setReviewFormError(form: HTMLFormElement, message: string) {
  const element = form.querySelector<HTMLElement>('[data-review-form-error]')

  if (!element) return

  element.textContent = message
  element.hidden = !message
}

function updateAddReviewSubmitState(
  form: HTMLFormElement,
  isSubmitting = false
) {
  const submitButton = form.querySelector<HTMLButtonElement>(
    'button[type="submit"]'
  )

  if (!submitButton) return

  const complete = hasAddReviewText(form) && hasAllReviewRatings(form)
  submitButton.disabled = isSubmitting || !complete
  const hint = form.querySelector<HTMLElement>('[data-review-form-hint]')
  if (hint) hint.hidden = complete
}

function hasAddReviewText(form: HTMLFormElement): boolean {
  return Boolean(getAddReviewTextarea(form)?.value.trim())
}

function getAddReviewTextarea(
  form: HTMLFormElement
): HTMLTextAreaElement | null {
  return form.querySelector<HTMLTextAreaElement>(
    'textarea[name="review[content]"]'
  )
}

function openReviewResultModal() {
  const modals = Modals.getInstance()
  const addReviewModal = document.querySelector<HTMLElement>(
    `[data-modal-name="${ADD_REVIEW_MODAL_NAME}"].is-active`
  )

  if (!addReviewModal) {
    modals.openModal(RESULT_MODAL_NAME)

    return
  }

  modals.closeModal(ADD_REVIEW_MODAL_NAME)
  window.setTimeout(() => {
    modals.openModal(RESULT_MODAL_NAME)
  }, RESULT_MODAL_OPEN_DELAY)
}

function fillProfileModal(data: CardData) {
  const modal = document.querySelector<HTMLElement>(
    `[data-modal-name="${MODAL_NAME}"]`
  )

  if (!modal) return

  fillModalQuestInfo(modal, data)

  const gamers = data.gamers ? Number(data.gamers) : null
  setText(modal, '[data-gamers-count]', gamers ? gamersText(gamers) : '')
  setText(modal, '[data-date-time]', data.date)
  setText(modal, '[data-quest-time]', data.time)
  setPhone(modal, data.phone)

  setText(
    modal,
    '[data-quest-address-metro]',
    data.metro ? `м. ${data.metro}` : ''
  )

  const addressMetroIcon = modal.querySelector<HTMLElement>(
    '[data-quest-address-metro-color]'
  )
  if (addressMetroIcon) {
    addressMetroIcon.style.color = data.metroColor || ''
    addressMetroIcon.classList.toggle('is-hidden', !data.metroColor)
  }

  setText(modal, '[data-quest-address-text]', data.address)
  setText(modal, '[data-quest-description]', data.addressDescription)

  fillShare(modal, data.socials || [])

  const reviewWrap = modal.querySelector<HTMLElement>(
    '[data-quest-review-wrap]'
  )
  if (reviewWrap) reviewWrap.hidden = !data.awaitingReview
}

function fillAddReviewModal(data: CardData) {
  const modal = document.querySelector<HTMLElement>(
    `[data-modal-name="${ADD_REVIEW_MODAL_NAME}"]`
  )

  if (!modal) return

  fillModalQuestInfo(modal, data)
  setText(modal, '[data-date-time]', data.date)
  setInputValue(modal, '[data-quest-id]', data.questId)
}

function fillModalQuestInfo(modal: HTMLElement, data: CardData) {
  modal
    .querySelectorAll<HTMLAnchorElement>('[data-quest-link]')
    .forEach((el) => {
      // Адрес приходит из карточки: собирать его здесь нельзя — у квеста в
      // пути есть город, и «/quests/:slug» отдавал 404.
      if (data.questUrl) {
        el.href = data.questUrl
        el.removeAttribute('aria-disabled')
      } else {
        el.removeAttribute('href')
        el.setAttribute('aria-disabled', 'true')
      }
    })

  setImage(modal, '[data-quest-image]', data.imageSrc)
  setText(modal, '[data-quest-name]', data.questName)
  setText(modal, '[data-quest-metro]', data.metro ? `м. ${data.metro}` : '')

  const metroIcon = modal.querySelector<HTMLElement>('[data-quest-metro-color]')
  if (metroIcon) {
    metroIcon.style.color = data.metroColor || ''
    metroIcon.classList.toggle('is-hidden', !data.metroColor)
  }

  setText(modal, '[data-quest-gamers]', data.gamersText)
  setText(modal, '[data-quest-length]', data.length)

  modal
    .querySelector<HTMLElement>('.profile-modal')
    ?.classList.toggle('is-coming', data.status === 'upcoming')
}

function setText(container: HTMLElement, selector: string, value?: string) {
  const element = container.querySelector<HTMLElement>(selector)

  if (element) {
    element.textContent = value || ''
  }
}

function setInputValue(
  container: HTMLElement,
  selector: string,
  value?: number | string
) {
  const input = container.querySelector<HTMLInputElement>(selector)

  if (input) {
    input.value = value?.toString() || ''
  }
}

function setImage(container: HTMLElement, selector: string, src?: string) {
  const image = container.querySelector<HTMLImageElement>(selector)

  if (!image) return

  image.src = src || ''
}

function setPhone(container: HTMLElement, phone?: string) {
  const phoneLink =
    container.querySelector<HTMLAnchorElement>('[data-quest-phone]')

  if (!phoneLink) return

  phoneLink.textContent = phone || ''
  // Пустой href у ссылки — это ссылка на текущую страницу: клик перезагружал
  // кабинет и закрывал модалку. Без телефона ссылки просто нет.
  if (phone) {
    phoneLink.href = `tel:${phone.replace(/[^\d+]/g, '')}`
  } else {
    phoneLink.removeAttribute('href')
  }
}

function parseSocials(raw?: string): SocialItem[] {
  if (!raw) return []

  try {
    return JSON.parse(raw) as SocialItem[]
  } catch {
    return []
  }
}

function fillShare(container: HTMLElement, items: SocialItem[]) {
  const list = container.querySelector<HTMLElement>('[data-quest-share-list]')
  const template = container.querySelector<HTMLTemplateElement>(
    '[data-quest-share-template]'
  )
  const wrap = container.querySelector<HTMLElement>('[data-quest-share-wrap]')

  if (!list || !template) return

  list.textContent = ''

  items.forEach((item) => {
    const fragment = template.content.cloneNode(true) as DocumentFragment
    const link = fragment.querySelector<HTMLAnchorElement>(
      '[data-quest-share-link]'
    )
    const icon = fragment.querySelector<SVGUseElement>(
      '[data-quest-share-icon]'
    )

    if (link) {
      link.href = item.url || '#'
      link.ariaLabel = item.name || ''
    }

    if (icon && item.icon) {
      icon.setAttribute('href', `#${item.icon}`)
    }

    list.appendChild(fragment)
  })

  wrap?.classList.toggle('is-hidden', !items.length)
}

function gamersText(count: number): string {
  return `${count} ${pluralize(count, ['игрок', 'игрока', 'игроков'])}`
}

function pluralize(count: number, forms: [string, string, string]): string {
  switch (new Intl.PluralRules('ru-RU').select(count)) {
    case 'one':
      return forms[0]
    case 'few':
      return forms[1]
    default:
      return forms[2]
  }
}
